import { useEffect, useRef } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { getRatingStarsHtml } from "../../../../helpers";
import { MAIN_FONT, TOOLTIP_COLORS } from "../../../../constants";

// declare module "highcharts" {
//   interface Point {
//     rating?: number;
//     ratingCount?: number;
//     avgPrice?: string;
//     won?: string;
//     lastWon?: string;
//     type?: string;
//   }
// }

export interface IPieChartData {
  name: string;
  y: number;
  rating: number;
  ratingCount: number;
  avgPrice: string;
  won: string;
  lastWon: string;
  type: string;
  color?: string;
}

interface IPieChartProps {
  data: IPieChartData[];
}

export const PieChart: React.FC<IPieChartProps> = ({ data }) => {
  const chartRef = useRef<HighchartsReact.RefObject>(null);

  useEffect(() => {
    if (chartRef.current) {
      chartRef.current.chart.redraw();
    }
  }, [data]);

  return (
    <HighchartsReact
      ref={chartRef}
      highcharts={Highcharts}
      options={{
        chart: {
          type: "pie",
          margin: [20, 20, 20, 20],
          spacing: [0, 0, 0, 0],
          height: "200",
        },
        tooltip: {
          useHTML: true,
          formatter: function (
            this: Highcharts.TooltipFormatterContextObject,
          ): string {
            const point = this.point as unknown as IPieChartData;
            const starsHtml = point.rating
              ? getRatingStarsHtml(point.rating)
              : "";
            return `
                  <div style="position: relative; display: flex; flex-direction: column; align-items: flex-start; width: 155px; line-height:1.1;">
                    <div style="width: 155px;">
                      <span style="display: block; line-height: 18px; word-break: break-all; overflow-wrap: break-word; white-space: normal;"><b>${point.name}</b></span>
                    </div>
                    ${
                      point?.rating && point?.rating > 0
                        ? `
                          <div style="display: flex; align-items: center; justify-content: space-between; width: 100%;">
                            <span style="line-height: 16px">Rating:</span>
                            <span style="line-height: 16px">${starsHtml}</span>
                          </div>
                          <div style="display: flex; align-items: center; justify-content: space-between; width: 100%;">
                            <span style="line-height: 16px">Rating Count:</span>
                            <span style="line-height: 16px">${point?.ratingCount}</span>
                          </div>
                        `
                        : ""
                    }
                    <div style="display: flex; align-items: center; justify-content: space-between; width: 100%;">
                      <span style="line-height: 16px">Avg. Price:</span>
                      <span style="line-height: 16px">${point?.avgPrice}</span>
                    </div>
                    <div style="display: flex; align-items: center; justify-content: space-between; width: 100%;">
                      <span style="line-height: 16px">Won:</span>
                      <span style="line-height: 16px">${point?.won}</span>
                    </div>
                    <div style="display: flex; align-items: center; justify-content: space-between; width: 100%;">
                      <span style="line-height: 16px">Last Won:</span>
                      <span style="line-height: 16px">${point?.lastWon}</span>
                    </div>
                    <div style="display: flex; align-items: center; justify-content: space-between; width: 100%;">
                      <span style="line-height: 16px">Type:</span>
                      <span style="line-height: 16px">${point?.type}</span>
                    </div>
                  </div>
                `;
          } as Highcharts.TooltipFormatterCallbackFunction,
          style: {
            color: TOOLTIP_COLORS.text,
            fontSize: "10px",
            fontFamily: MAIN_FONT,
          },
          shadow: false,
          borderColor: "transparent",
          backgroundColor: TOOLTIP_COLORS.background,
          borderRadius: 4,
        },
        plotOptions: {
          pie: {
            borderWidth: 0,
            slicedOffset: 0,
            borderColor: null,
            shadow: false,
          },
          series: {
            allowPointSelect: true,
            cursor: "pointer",
            dataLabels: [
              {
                enabled: true,
                distance: 20,
              },
              {
                enabled: false,
                distance: -40,
                format: "{point.percentage:.1f}%",
                style: {
                  fontSize: "12px",
                  textOutline: "none",
                  opacity: 0.7,
                },
                filter: {
                  operator: ">",
                  property: "percentage",
                  value: 10,
                },
              },
            ],
          },
        },
        series: [
          {
            name: "Percentage",
            colorByPoint: true,
            data,
          },
        ],
        title: {
          text: "",
        },
        credits: {
          enabled: false,
        },
      }}
    />
  );
};

export default PieChart;
